import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, Copy, Mail } from "@/components/Icons";
import { PatternOverlay } from "@/components/PatternOverlay";
import { Section } from "@/components/Section";
import { Button, ButtonLink } from "@/components/ui/Button";
import { Divider } from "@/components/ui/Divider";
import { GlassCard } from "@/components/ui/GlassCard";
import { Pill } from "@/components/ui/Pill";
import { SurfaceCard } from "@/components/ui/SurfaceCard";
import { cn } from "@/lib/cn";

const HeroCanvas = lazy(() => import("@/components/HeroCanvas"));

const CONTACT_EMAIL = "contact@everscale.is";
const DOMAIN = "https://everscale.is";

const CAPABILITIES = [
  {
    key: "build",
    label: "Build",
    items: ["Subscription UX & paywalls", "Performance-minded engineering", "Analytics foundations"]
  },
  {
    key: "launch",
    label: "Launch",
    items: ["App Store positioning", "ASO & creatives", "Instrumentation & funnels"]
  },
  {
    key: "grow",
    label: "Grow",
    items: ["Acquisition experiments", "Retention & pricing", "Iteration loops"]
  }
] as const;

type CapabilityKey = (typeof CAPABILITIES)[number]["key"];

export default function App() {
  const reduceMotion = useReducedMotion();
  const [pointer, setPointer] = useState({ x: 0, y: 0 });
  const pointerRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [capability, setCapability] = useState<CapabilityKey>("build");

  const heroRef = useRef<HTMLElement | null>(null);
  const heroInView = useInView(heroRef, { margin: "-25% 0px -25% 0px" });

  const year = useMemo(() => new Date().getFullYear(), []);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1400);
    return () => window.clearTimeout(t);
  }, [copied]);

  return (
    <div className="min-h-dvh text-fg">
      <header
        className={cn(
          "sticky top-0 z-40 border-b border-border/10 bg-bg/55 backdrop-blur-glass pt-[env(safe-area-inset-top)]",
          "supports-[backdrop-filter]:bg-bg/25"
        )}
      >
        <nav
          aria-label="Primary"
          className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 md:px-6"
        >
          <a
            href="#top"
            className={cn(
              "group inline-flex items-center gap-2 rounded-xl px-2 py-1",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/55"
            )}
          >
            <img
              src="/logo-mark.svg"
              alt=""
              aria-hidden="true"
              className="h-7 w-7"
              loading="eager"
              decoding="async"
            />
            <span className="text-base font-semibold tracking-tight text-fg/90">
              Everscale
            </span>
            <span className="h-1 w-1 rounded-full bg-accent2/80 opacity-60 transition group-hover:opacity-100" />
          </a>

          <ButtonLink
            href={`mailto:${CONTACT_EMAIL}`}
            size="sm"
            className={cn(
              "shadow-glow backdrop-blur-glass",
              "border-border/12 bg-panel/20 hover:border-border/18 hover:bg-panel/28"
            )}
          >
            <Mail className="h-4 w-4 opacity-80" />
            Contact
          </ButtonLink>
        </nav>
      </header>

      <main id="main" className="relative">
        <section
          id="top"
          ref={heroRef}
          className="relative isolate overflow-hidden"
          onPointerMove={(e) => {
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            const px = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            const py = ((e.clientY - rect.top) / rect.height) * 2 - 1;
            pointerRef.current = { x: px, y: -py };
            if (rafRef.current) return;
            rafRef.current = requestAnimationFrame(() => {
              rafRef.current = null;
              setPointer(pointerRef.current);
            });
          }}
          onPointerLeave={() => {
            pointerRef.current = { x: 0, y: 0 };
            setPointer({ x: 0, y: 0 });
          }}
        >
          <div className="absolute inset-0 -z-10">
            <Suspense fallback={null}>
              <HeroCanvas
                pointer={pointer}
                reducedMotion={!!reduceMotion}
                active={heroInView}
              />
            </Suspense>

            <div
              aria-hidden="true"
              className={cn(
                "absolute inset-0",
                "bg-[radial-gradient(1000px_700px_at_35%_10%,rgba(10,132,255,0.10),transparent_60%)]",
                "opacity-80"
              )}
            />
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-b from-bg/10 via-bg/60 to-bg/92"
            />
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-[radial-gradient(1400px_900px_at_50%_20%,rgba(0,200,255,0.06),transparent_60%)]"
            />
            <PatternOverlay />
          </div>

          <div className="mx-auto grid min-h-[88svh] max-w-6xl items-center gap-10 px-4 pb-14 pt-20 md:px-6 md:pb-20 md:pt-28 lg:grid-cols-12 lg:gap-12">
            <motion.div
              initial={reduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-[44rem] lg:col-span-7"
            >
              <h1 className="leading-none">
                <span className="sr-only">Everscale</span>
                <img
                  src="/logo-hero.svg"
                  alt=""
                  aria-hidden="true"
                  className="h-[clamp(56px,7vw,120px)] w-auto max-w-full"
                  loading="eager"
                  decoding="async"
                />
              </h1>

              <p className="mt-5 text-pretty text-[17px] leading-relaxed text-muted/90 md:text-xl">
                We build and grow subscription iOS apps with calm product craft, sharp
                App Store execution, and performance marketing.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <ButtonLink
                  href={`mailto:${CONTACT_EMAIL}`}
                  variant="primary"
                  className="group"
                >
                  Contact
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </ButtonLink>

                <ButtonLink
                  href="#what-we-do"
                  variant="secondary"
                  className="border-border/12 bg-panel/14 hover:border-border/18 hover:bg-panel/18"
                  onClick={(e) => {
                    const id = "what-we-do";
                    const el = document.getElementById(id);
                    if (el) {
                      e.preventDefault();
                      el.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth" });
                    }
                  }}
                >
                  What we do
                </ButtonLink>
              </div>

              {/* Trust pills intentionally removed: the logo already carries the tagline,
                  and the right capability card provides the product-like “what we do” layer. */}
            </motion.div>

            <motion.div
              initial={reduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
              transition={{ duration: 0.65, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-5"
            >
              <CapabilitiesCard value={capability} onChange={setCapability} />
            </motion.div>
          </div>
        </section>

        <Section
          id="what-we-do"
          title="What we do"
          eyebrow="Services"
          description="Build, launch, and scale—focused, senior delivery."
        >
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <ServiceCard
              title="Build"
              items={["Subscription UX & paywalls", "Performance-minded engineering", "Analytics foundations"]}
              delay={0.0}
            />
            <ServiceCard
              title="Launch"
              items={["App Store positioning", "ASO & creatives", "Instrumentation & funnels"]}
              delay={0.05}
            />
            <ServiceCard
              title="Grow"
              items={["Acquisition experiments", "Retention & pricing", "Iteration loops"]}
              delay={0.1}
            />
          </div>
        </Section>

        <Section
          id="how-we-work"
          title="How we work"
          eyebrow="Approach"
          description="A simple loop: clarity, shipping, iteration."
        >
          <ol className="relative grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:before:pointer-events-none lg:before:absolute lg:before:left-6 lg:before:right-6 lg:before:top-11 lg:before:h-px lg:before:bg-gradient-to-r lg:before:from-accent/0 lg:before:via-accent2/25 lg:before:to-accent/0 lg:before:content-['']">
            <StepRow
              n="01"
              title="Discovery"
              body="Define the product edge, audience, and success metrics."
              delay={0.0}
            />
            <StepRow
              n="02"
              title="Build"
              body="Ship fast with clean systems and premium UX details."
              delay={0.05}
            />
            <StepRow
              n="03"
              title="Scale"
              body="Iterate with ASO, pricing, and acquisition experiments."
              delay={0.1}
            />
          </ol>
        </Section>

        <Section
          id="contact"
          title="Contact"
          eyebrow="Let’s talk"
          description="A quick email is enough to start."
        >
          <GlassCard className="p-6 md:p-8">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted/85">
                  Email
                </p>
                <p className="mt-2 text-lg font-semibold tracking-tight text-fg">
                  {CONTACT_EMAIL}
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <ButtonLink
                  href={`mailto:${CONTACT_EMAIL}`}
                  variant="primary"
                >
                  <Mail className="h-4 w-4" />
                  Open mail
                </ButtonLink>

                <Button
                  className={cn(
                    "border-border/14 bg-panel/18 hover:border-border/20 hover:bg-panel/26"
                  )}
                  onClick={async () => {
                    const text = CONTACT_EMAIL;
                    try {
                      await navigator.clipboard.writeText(text);
                      setCopied(true);
                      return;
                    } catch {
                      // Fallback: create temporary input for older browsers.
                      const input = document.createElement("input");
                      input.value = text;
                      input.setAttribute("readonly", "true");
                      input.style.position = "absolute";
                      input.style.left = "-9999px";
                      document.body.appendChild(input);
                      input.select();
                      document.execCommand("copy");
                      document.body.removeChild(input);
                      setCopied(true);
                    }
                  }}
                  aria-label="Copy email address"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      Copy email
                    </>
                  )}
                </Button>
                <span className="sr-only" aria-live="polite">
                  {copied ? "Email copied to clipboard." : ""}
                </span>
              </div>
            </div>
          </GlassCard>
        </Section>

        <footer className="border-t border-border/10">
          <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-8 text-base text-muted md:flex-row md:items-center md:justify-between md:px-6">
            <p>© {year} Everscale.</p>
            <a
              href={DOMAIN}
              className="inline-flex items-center gap-2 text-fg/80 transition hover:text-fg"
            >
              {DOMAIN.replace("https://", "")}
              <ArrowRight className="h-4 w-4 opacity-70" />
            </a>
          </div>
        </footer>
      </main>
    </div>
  );
}

function ServiceCard({
  title,
  items,
  delay
}: {
  title: string;
  items: string[];
  delay: number;
}) {
  const reduceMotion = useReducedMotion();
  return (
    <SurfaceCard
      initial={reduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      className="group p-6"
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-base font-medium text-fg">{title}</h3>
        <div
          aria-hidden="true"
          className="h-9 w-9 rounded-2xl bg-gradient-to-br from-accent/18 to-accent2/12 ring-1 ring-border/8"
        />
      </div>
      <Divider className="mt-4" />
      <ul className="mt-5 space-y-2 text-sm text-muted">
        {items.map((item) => (
          <li key={item} className="flex gap-2">
            <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 rounded-full bg-accent2/70" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </SurfaceCard>
  );
}

function StepRow({
  n,
  title,
  body,
  delay
}: {
  n: string;
  title: string;
  body: string;
  delay: number;
}) {
  const reduceMotion = useReducedMotion();
  return (
    <SurfaceCard
      initial={reduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      className="p-6"
    >
      <div className="flex items-start gap-4">
        <div
          aria-hidden="true"
          className={cn(
            "mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-2xl",
            "border border-border/12 bg-panel/16 text-[11px] font-semibold text-fg/80"
          )}
        >
          {n}
        </div>
        <div>
          <h3 className="text-base font-semibold tracking-tight text-fg">{title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted/90">{body}</p>
        </div>
      </div>
    </SurfaceCard>
  );
}

function CapabilitiesCard({
  value,
  onChange
}: {
  value: CapabilityKey;
  onChange: (v: CapabilityKey) => void;
}) {
  const active = CAPABILITIES.find((c) => c.key === value) ?? CAPABILITIES[0];
  return (
    <GlassCard className="p-6">
      <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted/85">
        Capabilities
      </p>

      <div className="mt-4">
        <SegmentedControl value={value} onChange={onChange} />
      </div>

      <div className="mt-5">
        <h3 className="text-sm font-semibold tracking-tight text-fg">{active.label}</h3>
        <ul className="mt-3 space-y-2 text-sm text-muted/90">
          {active.items.map((item) => (
            <li key={item} className="flex gap-2">
              <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 rounded-full bg-accent2/70" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <Divider className="mt-6" />

      <div className="mt-4 flex flex-wrap gap-2">
        <Pill className="px-3 py-1.5">iOS only</Pill>
        <Pill className="px-3 py-1.5">Subscription UX</Pill>
        <Pill className="px-3 py-1.5">ASO & acquisition</Pill>
      </div>
    </GlassCard>
  );
}

function SegmentedControl({
  value,
  onChange
}: {
  value: CapabilityKey;
  onChange: (v: CapabilityKey) => void;
}) {
  const reduceMotion = useReducedMotion();
  return (
    <div
      className="flex w-full rounded-full border border-border/12 bg-panel/10 p-1"
      role="tablist"
      aria-label="Capabilities"
    >
      {CAPABILITIES.map((opt) => {
        const isActive = opt.key === value;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange(opt.key)}
            role="tab"
            aria-selected={isActive}
            className={cn(
              "relative flex-1 rounded-full px-3 py-2 text-[12px] font-semibold",
              "transition",
              isActive ? "text-fg" : "text-fg/70 hover:text-fg/85",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/55"
            )}
          >
            {isActive ? (
              reduceMotion ? (
                <span className="absolute inset-0 rounded-full border border-border/10 bg-panel/18" />
              ) : (
                <motion.span
                  layoutId="cap-seg"
                  className="absolute inset-0 rounded-full border border-border/10 bg-panel/18"
                  transition={{ type: "spring", stiffness: 420, damping: 34 }}
                />
              )
            ) : null}
            <span className="relative z-10">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}
